import { forwardRef, useState, type InputHTMLAttributes } from 'react';
import { cn } from '@/shared/lib/cn';
import { Icon } from '@/shared/ui/icon';

export interface InputProps extends InputHTMLAttributes<HTMLInputElement> {
  invalid?: boolean;
}

const baseClasses =
  'h-10 w-full rounded-md border bg-[var(--field-surface)] px-3 text-base ' +
  'text-[var(--text-primary)] placeholder:text-[var(--text-muted)] ' +
  'transition-colors duration-fast ease-standard ' +
  'focus-visible:border-[var(--field-focus-border)] focus-visible:outline-none ' +
  'focus-visible:ring-2 focus-visible:ring-[var(--field-focus-ring)] ' +
  'disabled:cursor-not-allowed disabled:bg-[var(--field-surface-disabled)] disabled:opacity-60';

export const Input = forwardRef<HTMLInputElement, InputProps>(function Input(
  { className, invalid = false, type, style, ...rest },
  ref,
) {
  const [revealed, setRevealed] = useState(false);
  const isPassword = type === 'password';
  const effectiveType = isPassword && revealed ? 'text' : (type ?? 'text');

  const field = (
    <input
      ref={ref}
      type={effectiveType}
      aria-invalid={invalid || undefined}
      // Inline padding so the reveal button never overlaps the value, regardless
      // of Tailwind class ordering.
      style={isPassword ? { paddingInlineEnd: '2.5rem', ...style } : style}
      className={cn(
        baseClasses,
        invalid
          ? 'border-[var(--danger-border)]'
          : 'border-[var(--border-subtle)] hover:border-[var(--border-strong)]',
        className,
      )}
      {...rest}
    />
  );

  if (!isPassword) return field;

  return (
    <div className="relative w-full">
      {field}
      <button
        type="button"
        onClick={() => setRevealed((v) => !v)}
        aria-label={revealed ? 'Hide password' : 'Show password'}
        aria-pressed={revealed}
        className="absolute inset-y-0 end-0 flex items-center px-3 text-[var(--text-muted)] transition-colors hover:text-[var(--text-secondary)] focus-visible:outline-none focus-visible:text-[var(--text-secondary)]"
      >
        <Icon name={revealed ? 'eye-off' : 'eye'} size={18} />
      </button>
    </div>
  );
});
